import axios from 'axios';
import { clearProductsCache } from '../hooks/useProducts';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const API_URL = `${BASE_URL}/api`;
const TOKEN_KEY = 'grant_crm_token';
const USER_KEY = 'grant_crm_user';

// Prevent concurrent logout redirects when multiple requests 401 simultaneously
let _isHandling401 = false;

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'ADMIN' | 'OPERATOR' | 'CLIENT';
  firstName?: string;
  lastName?: string;
  mustChangePassword?: boolean;
  isTemporaryPassword?: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
}

/**
 * Auth Service - handles authentication and token management
 */
export const authService = {
  /**
   * Login with username/email and password
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${API_URL}/auth/login`, {
        username,
        password,
      });

      const { token, user } = response.data;

      // Store token and user info in localStorage
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Nieprawidłowa nazwa użytkownika lub hasło');
      }
      throw new Error(error.response?.data?.error || 'Błąd logowania');
    }
  },

  /**
   * Logout - clear token and user info
   */
  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await axios.post(
          `${API_URL}/auth/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      clearProductsCache();
    }
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Get JWT token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  },

  /**
   * Parse JWT expiry time (exp claim) without verifying signature.
   */
  getTokenExpiry(): Date | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch {
      return null;
    }
  },

  /**
   * Returns true if the stored token is expired.
   */
  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    return expiry ? expiry < new Date() : false;
  },

  /**
   * Returns true if the token expires within the given minutes.
   */
  isTokenExpiringSoon(withinMinutes = 10): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false;
    return expiry < new Date(Date.now() + withinMinutes * 60 * 1000);
  },

  /**
   * Ask backend to issue a fresh token (proactive renewal — token must still be valid).
   * Stores the new token in localStorage.
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response = await axios.post<{ token: string }>(`${API_URL}/auth/refresh`);
      const { token } = response.data;
      localStorage.setItem(TOKEN_KEY, token);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Check if user has specific role
   */
  hasRole(role: 'ADMIN' | 'OPERATOR' | 'CLIENT'): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  },

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: Array<'ADMIN' | 'OPERATOR' | 'CLIENT'>): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  },

  /**
   * Fetch current user info from API
   */
  async fetchCurrentUser(): Promise<User> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await axios.get<User>(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update stored user info
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token is invalid or expired, clear auth data
        this.logout();
      }
      throw new Error(error.response?.data?.error || 'Nie udało się pobrać danych użytkownika');
    }
  },

  /**
   * Login with Google OAuth
   */
  async googleLogin(idToken: string, email: string, firstName: string, lastName: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${API_URL}/auth/google`, {
        idToken,
        email,
        firstName,
        lastName,
      });

      const { token, user } = response.data;

      // Store token and user info in localStorage
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Twój adres email nie ma uprawnień do logowania przez Google. Skontaktuj się z administratorem.');
      }
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Google authentication failed');
    }
  },

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update user info to clear must_change_password flag
      const user = this.getCurrentUser();
      if (user) {
        user.mustChangePassword = false;
        user.isTemporaryPassword = false;
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Nie udało się zmieńić hasła');
    }
  },

  /**
   * Setup axios interceptor to add auth token to all requests
   */
  setupAxiosInterceptor() {
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor for handling 401 responses (with race-condition guard)
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !_isHandling401) {
          _isHandling401 = true;
          await this.logout();
          window.location.href = '/login';
          // Reset after navigation gives time to complete
          setTimeout(() => { _isHandling401 = false; }, 3000);
        }
        return Promise.reject(error);
      }
    );
  },
};

// Setup interceptor when module loads
authService.setupAxiosInterceptor();
